import React, { useState, useRef, useEffect, useCallback } from "react";
import { Typography, InputBase } from "@mui/material";
import ArrowIcon from "../../../../assets/drop-down.svg";
import {
  StyledDropdownContainer,
  StyledDropdownButton,
  StyledDropdownLabel,
  StyledDropdownArrowContainer,
  StyledDropdownMenu,
  StyledDropdownScrollArea,
  StyledDropdownItem,
} from "../styles";
import {
  getCustomersForDashboardAPI,
  type Customer,
} from "../../../../api/getCustomersAPI";

interface SelectClientDropdownProps {
  selectedClient: string;
  setSelectedClient: (email: string) => void;
  disabled?: boolean;
  isClientAnalytics?: boolean;
  sortByStatus?: boolean;
  disableTopPadding?: boolean;
}

const SelectClientDropdown: React.FC<SelectClientDropdownProps> = ({
  selectedClient,
  setSelectedClient,
  disabled = false,
  isClientAnalytics = false,
  sortByStatus = false,
  disableTopPadding = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [clients, setClients] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClientLabel, setSelectedClientLabel] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const sortClientsForClientAnalytics = (customers: Customer[]) => {
    const statusOrder: Record<string, number> = {
      active: 0,
      lapsed: 1,
      inactive: 2,
    };

    return [...customers].sort((a, b) => {
      const aRank = statusOrder[(a.status || "").toLowerCase()] ?? 999;
      const bRank = statusOrder[(b.status || "").toLowerCase()] ?? 999;

      return aRank - bRank;
    });
  };

  const dedupeById = (arr: Customer[]) => {
    const map = new Map<string, Customer>();
    for (const c of arr) {
      if (!c?.id) continue;
      if (!map.has(c.id)) map.set(c.id, c);
    }
    return Array.from(map.values());
  };

  const fetchClients = useCallback(
    async (search = "") => {
      try {
        const data = await getCustomersForDashboardAPI(search);
        const dedupedCustomers = dedupeById(data.customers || []);

        setClients(
          sortByStatus
            ? sortClientsForClientAnalytics(dedupedCustomers)
            : dedupedCustomers
        );
      } catch {
        setClients([]);
      }
    },
    [sortByStatus]
  );

  const handleToggle = () => {
    if (disabled) return;

    if (!isOpen) {
      fetchClients("");
      setTimeout(() => inputRef.current?.focus(), 100);
    }

    setIsOpen((prev) => !prev);
  };

  const handleSelect = (email: string, outletName?: string) => {
    if (disabled) return;
    setSelectedClient(email);
    setSelectedClientLabel(email ? outletName || "" : "");
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchClients(searchTerm);
    }, 350);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, isOpen, fetchClients]);

  useEffect(() => {
    if (!selectedClient) {
      setSelectedClientLabel("");
      return;
    }

    const selected = clients.find((c) => c.emailId === selectedClient);

    if (selected?.outletName) {
      setSelectedClientLabel(selected.outletName);
      return;
    }

    const isSelectedClientLoaded = clients.some(
      (c) => c.emailId === selectedClient
    );

    if (!isSelectedClientLoaded) {
      fetchClients("");
    }
  }, [selectedClient, clients, fetchClients]);

  const getSelectedLabel = (): string => {
    if (!selectedClient) {
      return isClientAnalytics ? "Select Customer" : "All Customers";
    }

    return (
      selectedClientLabel ||
      (isClientAnalytics ? "Select Customer" : "All Customers")
    );
  };

  return (
    <StyledDropdownContainer
      ref={dropdownRef}
      sx={disableTopPadding ? { pt: 0 } : undefined}
    >
      <StyledDropdownButton
        onClick={handleToggle}
        disabled={disabled}
        disableRipple
        disableFocusRipple
        isClientAnalytics={isClientAnalytics}
      >
        {isOpen ? (
          <InputBase
            inputRef={inputRef}
            fullWidth
            placeholder="Search customer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            sx={{ fontSize: "14px" }}
            type="text"
          />
        ) : (
          <StyledDropdownLabel>{getSelectedLabel()}</StyledDropdownLabel>
        )}

        <StyledDropdownArrowContainer isOpen={isOpen}>
          <img
            src={ArrowIcon}
            alt="dropdown-arrow"
            style={{ width: "10px", height: "10px" }}
          />
        </StyledDropdownArrowContainer>
      </StyledDropdownButton>

      {isOpen && (
        <StyledDropdownMenu>
          <StyledDropdownScrollArea>
            {!isClientAnalytics && (
              <StyledDropdownItem onClick={() => handleSelect("")}>
                <Typography variant="body2">All Customers</Typography>
              </StyledDropdownItem>
            )}

            {clients
              .filter((client) =>
                searchTerm.trim() === ""
                  ? true
                  : (client.outletName || "")
                      .toLowerCase()
                      .includes(searchTerm.trim().toLowerCase())
              )
              .map((client) => (
                <StyledDropdownItem
                  key={client.id}
                  onClick={() =>
                    handleSelect(client.emailId || "", client.outletName || "")
                  }
                >
                  <Typography variant="body2">{client.outletName}</Typography>
                </StyledDropdownItem>
              ))}

            {clients.length === 0 && (
              <Typography variant="body2" sx={{ p: 1 }}>
                No customers found
              </Typography>
            )}
          </StyledDropdownScrollArea>
        </StyledDropdownMenu>
      )}
    </StyledDropdownContainer>
  );
};

export default SelectClientDropdown;
